import { useCallback, useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useListingLoader } from "../../../../components/hooks/ListingHooks";
import SelectionDeleter from "../../../../components/TableComponents/SelectionDeleter";
import { deleteOrg, editTeam, getProfile } from "../../../../api/UserAPI";
import ItemDeleter from "../../../../components/TableComponents/ItemDeleter";
import {
  ActionIcon,
  Center,
  Group,
  SegmentedControl,
  Tooltip,
} from "@mantine/core";
import dateColumn from "../../../../layouts/Table/columns/dateColumn";
import numberColumn from "../../../../layouts/Table/columns/numberColumn";
import { nameColumn } from "../../../../layouts/Table/columns/nameColumn";
import MainTable from "../../../../layouts/Table/MainTable";
import TableLayoutBlock from "../../../../layouts/Table/TableLayoutBlock";
import { IconDoorExit, IconUser, IconWorld } from "@tabler/icons-react";
import { CommCode } from "../CommCode";
import CreateTeamButton from "./CreateOrgButton";
import { useProfileStore } from "../../../../stores/UserStore";
import { getOrgs } from "../../../../api/AdminAPI";
import InviteToCommunity from "../InviteToCommunity";
import PageTitle from "../../../../components/View/PageTitle";
import { sortDescId } from "../../../../functions/utilFunctions";

const OrgListing = () => {
  const apiRef = useGridApiRef();
  const lang = useLanguage((s) => s.language);
  const profile = useProfileStore((s) => s.profile);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const columns = useMemo(
    () => [
      numberColumn(),
      nameColumn({
        parentPath: (row) => `/community/organizations/${row.id}/users`,
        withoutId: true,
        editFunction: () => editTeam,
        getDisabled: () => false,
        apiRef,
      }),
      numberColumn(
        "number_of_users",
        lang.resellers.columns.number_of_users,
        100
      ),
      {
        field: "user_level",
        headerName: lang.resellers.columns.user_level,
        width: 120,
        valueGetter: ({ value }) => {
          if (value === "user") {
            return lang.resellers.columns.user_level_value_user;
          }
          if (value === "admin") {
            return lang.resellers.columns.user_level_value_admin;
          }
          if (value === "super_admin") {
            return lang.resellers.columns.user_level_value_super_admin;
          }
        },
      },
      {
        field: "organization_code",
        headerName: lang.organizations.columns.organization_code,
        width: 150,
        renderCell: ({ value }) => {
          return <CommCode token={value} />;
        },
      },
      dateColumn(),
    ],
    [apiRef, lang]
  );

  const actionsCell = useCallback(
    ({ row }) => {
      return (
        <Group wrap="nowrap" gap={4}>
          <Tooltip label="Quitter">
            <ActionIcon variant="subtle" disabled>
              <IconDoorExit size={18} />
            </ActionIcon>
          </Tooltip>
          <InviteToCommunity current={row} isAction isOrg />
          <ItemDeleter
            title_bold={`${row.name} #${row.id}`}
            handleDelete={deleteOrg}
            deletedMessage={lang.organizations.delete_success}
            id={row.id}
            apiRef={apiRef}
          />
        </Group>
      );
    },
    [apiRef, lang.organizations.delete_success]
  );

  const toolbarActions = useCallback(
    ({ selectedIds }) => (
      <SelectionDeleter
        handleDelete={deleteOrg}
        apiRef={apiRef}
        setTableLoading={setLoading}
        ids={selectedIds}
      />
    ),
    [apiRef]
  );

  const [type, setType] = useState(
    profile?.level === "admin" ? "admin" : "user"
  );

  useListingLoader({
    loading,
    setLoading,
    listGetter: type === "admin" ? getOrgs : getProfile,
    responseField: type === "admin" ? "organizations_list" : "user_profile",
    setRows,
    getRows: (data) =>
      type === "admin" ? sortDescId(data) : sortDescId(data.organizations),
  });

  useEffect(() => {
    setLoading(true);
  }, [type]);

  return (
    <>
      <Group
        className="layout-block top"
        w={"100%"}
        gap={0}
        justify="space-between"
      >
        <PageTitle title={lang.organizations.browser_tab_title} />

        {profile?.level === "admin" && (
          <SegmentedControl
            ml={"auto"}
            mr={16}
            data={[
              {
                label: (
                  <Center>
                    <IconWorld size={16} style={{ marginRight: 8 }} />
                    {lang.organizations.admin_organization_list_main_help}
                  </Center>
                ),
                value: "admin",
              },
              {
                label: (
                  <Center>
                    <IconUser size={16} style={{ marginRight: 8 }} />
                    {lang.organizations.user_organization_list_title}
                  </Center>
                ),
                value: "user",
              },
            ]}
            onChange={setType}
          />
        )}

        <CreateTeamButton />
      </Group>

      <TableLayoutBlock>
        <MainTable
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          loading={loading}
          setLoading={setLoading}
          id={"list-table"}
          toolbarActions={toolbarActions}
          actionsCell={actionsCell}
          pageOrderName={"organizations_order"}
        />
      </TableLayoutBlock>
    </>
  );
};

export default OrgListing;
